import React, { useEffect, useState, useCallback } from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import RefreshIcon from "@mui/icons-material/Refresh";
import CheckIcon from "@mui/icons-material/Check";
import ClearIcon from "@mui/icons-material/Clear";
import TextField from "@mui/material/TextField";

import Layout from "../../layout";
import CustomDataTable from "../../components/table/CustomTable";
import CustomOutlinedSelect from "../../forms/select/OutlinedCustomSelect";
import {
  getAdjustmentsV2,
  approveAdjustmentV2,
  rejectAdjustmentV2,
  AdjustmentV2,
} from "../../utils/adjustmentServiceV2";
import { fetchGETRequest } from "../../utils/services";
import ConfirmationModal from "../../components/modals/ConfirmationModal";
import StatusModal from "../../components/modals/StatusModal";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";

const AdjustmentsV2Page: React.FC = () => {
  const [adjustments, setAdjustments] = useState<AdjustmentV2[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // Filters
  const [status, setStatus] = useState<string>("");
  const [adjustmentType, setAdjustmentType] = useState<string>("");
  const [customerId, setCustomerId] = useState<string>("");
  const [customers, setCustomers] = useState<any[]>([]);

  // Actions
  const [pendingAction, setPendingAction] = useState<{
    adjustment: AdjustmentV2;
    type: "approve" | "reject";
  } | null>(null);
  const [rejectionReason, setRejectionReason] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  const [statusModal, setStatusModal] = useState<{
    open: boolean;
    type: "success" | "error";
    message: string;
    title: string;
  }>({
    open: false,
    type: "success",
    message: "",
    title: "",
  });

  const fetchAdjustments = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getAdjustmentsV2({
        page,
        pageSize: rowsPerPage,
        status: status || undefined,
        adjustmentType: adjustmentType || undefined,
        customerId: customerId || undefined,
      });

      if (response && response.success) {
        setAdjustments(response.data);
        setTotalCount(response.totalCount);
      }
    } catch (error) {
      console.error("Error fetching adjustments:", error);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, status, adjustmentType, customerId]);

  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const res: any = await fetchGETRequest(
          "customers?pageSize=1000&page=0",
          {},
          true,
        );
        if (res && res.data && res.data.data) {
          setCustomers(
            res.data.data.map((c: any) => ({
              label: `${c.name} (${c.additionalInfo?.consumerId || c.title || ""})`,
              value: c.id.id,
            })),
          );
        }
      } catch (err) {
        console.error("Error fetching customers", err);
      }
    };
    fetchCustomers();
  }, []);

  useEffect(() => {
    fetchAdjustments();
  }, [fetchAdjustments]);

  const handlePageChange = (event: any, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleProcessAction = async () => {
    if (!pendingAction) return;

    const { adjustment, type } = pendingAction;
    setActionLoading(true);
    try {
      let res;
      if (type === "approve") {
        res = await approveAdjustmentV2(adjustment.id, {
          approvedBy: "00000000-0000-0000-0000-000000000000",
        });
      } else {
        if (!rejectionReason.trim()) {
          alert("Please provide a rejection reason");
          setActionLoading(false);
          return;
        }
        res = await rejectAdjustmentV2(adjustment.id, {
          rejectedBy: "00000000-0000-0000-0000-000000000000",
          rejectionReason,
        });
      }

      if (res && res.success) {
        setStatusModal({
          open: true,
          type: "success",
          title: type === "approve" ? "Approved" : "Rejected",
          message: `Adjustment has been ${type}d successfully`,
        });
        setPendingAction(null);
        setRejectionReason("");
        fetchAdjustments();
      } else {
        throw res;
      }
    } catch (err: any) {
      console.error(`Error ${type}ing adjustment`, err);
      setStatusModal({
        open: true,
        type: "error",
        title: "Action Failed",
        message: err.message || `Failed to ${type} adjustment`,
      });
    } finally {
      setActionLoading(false);
    }
  };

  const columns = [
    "ADJ Number",
    "Bill Number",
    "Customer",
    "Type",
    "Amount",
    "Reason",
    "Status",
    "Actions",
  ];

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case "approved":
        return "success";
      case "pending":
        return "warning";
      case "rejected":
        return "error";
      default:
        return "default";
    }
  };

  const rowsContent = adjustments.map((adj) => (
    <TableRow key={adj.id}>
      <TableCell>{adj.adjustmentNumber}</TableCell>
      <TableCell>{adj.billNumber}</TableCell>
      <TableCell>{adj.customerName}</TableCell>
      <TableCell>
        <Chip
          label={adj.adjustmentType}
          size="small"
          variant="outlined"
          color={adj.adjustmentType === "Credit" ? "success" : "primary"}
        />
      </TableCell>
      <TableCell>₹{adj.amount?.toLocaleString()}</TableCell>
      <TableCell
        sx={{
          maxWidth: 200,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {adj.reason}
      </TableCell>
      <TableCell>
        <Chip
          label={adj.status}
          size="small"
          color={getStatusColor(adj.status) as any}
        />
      </TableCell>
      <TableCell>
        {adj.status?.toLowerCase() === "pending" && (
          <Stack direction="row" spacing={1}>
            <IconButton
              size="small"
              color="success"
              onClick={() =>
                setPendingAction({ adjustment: adj, type: "approve" })
              }
              title="Approve"
            >
              <CheckIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              color="error"
              onClick={() =>
                setPendingAction({ adjustment: adj, type: "reject" })
              }
              title="Reject"
            >
              <ClearIcon fontSize="small" />
            </IconButton>
          </Stack>
        )}
      </TableCell>
    </TableRow>
  ));

  return (
    <Layout>
      <Box sx={{ p: 3 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
        >
          <Typography variant="h6" fontWeight="bold">
            Bill Adjustments (v2)
          </Typography>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchAdjustments}
            sx={{ borderRadius: "8px", textTransform: "none" }}
          >
            Refresh
          </Button>
        </Stack>

        <Card sx={{ p: 2, mb: 3, borderRadius: "12px" }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <CustomOutlinedSelect
              label="Status"
              width="200px"
              options={[
                { label: "All Status", value: "" },
                { label: "Pending", value: "Pending" },
                { label: "Approved", value: "Approved" },
                { label: "Rejected", value: "Rejected" },
              ]}
              value={status}
              onChange={(opt) => {
                setStatus(opt.value as string);
                setPage(0);
              }}
            />
            <CustomOutlinedSelect
              label="Type"
              width="200px"
              options={[
                { label: "All Types", value: "" },
                { label: "Credit", value: "Credit" },
                { label: "Debit", value: "Debit" },
              ]}
              value={adjustmentType}
              onChange={(opt) => {
                setAdjustmentType(opt.value as string);
                setPage(0);
              }}
            />
            <CustomOutlinedSelect
              label="Customer"
              width="250px"
              options={[{ label: "All Customers", value: "" }, ...customers]}
              value={customerId}
              onChange={(opt) => {
                setCustomerId(opt.value as string);
                setPage(0);
              }}
              placeholder="Select Customer"
            />
          </Stack>
        </Card>

        <Card
          sx={{
            borderRadius: "12px",
            boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.05)",
          }}
        >
          <CustomDataTable
            columns={columns}
            rowsContent={rowsContent}
            isLoading={loading}
            pagination
            totalCount={totalCount}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
            Actions
          />
        </Card>
      </Box>

      {/* Approve Confirmation */}
      <ConfirmationModal
        confirmModal={pendingAction?.type === "approve"}
        title="Approve Adjustment"
        body={`Are you sure you want to approve adjustment ${pendingAction?.adjustment?.adjustmentNumber}?`}
        onClose={() => setPendingAction(null)}
        onClick={handleProcessAction}
        isLoading={actionLoading}
      />

      {/* Reject Dialog */}
      <Dialog
        open={pendingAction?.type === "reject"}
        onClose={() => setPendingAction(null)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Reject Adjustment</DialogTitle>
        <DialogContent>
          <Typography variant="body2" mb={2}>
            Please provide a reason for rejecting adjustment{" "}
            {pendingAction?.adjustment?.adjustmentNumber}.
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Rejection Reason"
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            variant="outlined"
            size="small"
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setPendingAction(null)} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleProcessAction}
            variant="contained"
            color="error"
            disabled={actionLoading}
            sx={{ borderRadius: "8px", textTransform: "none" }}
          >
            {actionLoading ? "Rejecting..." : "Reject Adjustment"}
          </Button>
        </DialogActions>
      </Dialog>

      <StatusModal
        open={statusModal.open}
        type={statusModal.type}
        title={statusModal.title}
        message={statusModal.message}
        onClose={() => setStatusModal({ ...statusModal, open: false })}
      />
    </Layout>
  );
};

export default AdjustmentsV2Page;
